import { useState } from "react";
import { PageHeader, GlassCard, EmptyState, useCrud, ItemDialog, RowActions } from "../_shared";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";

const FIELDS = [
  { key: "tag", label: "Tag / Eyebrow", required: true, placeholder: "e.g. Instant Activation" },
  { key: "title", label: "Title", required: true, placeholder: "e.g. New FASTag in 15 Minutes" },
  { key: "subtitle", label: "Subtitle", type: "textarea", rows: 2 },
  { key: "image", label: "Banner Image URL", required: true, type: "image", help: "Paste an https:// URL. Ideal 1600×900." },
  { key: "cta_label", label: "CTA button label", placeholder: "e.g. Buy New FASTag" },
  { key: "cta_to", label: "CTA link (path or URL)", placeholder: "e.g. /order?service=New%20FASTag" },
  { key: "enabled", label: "Visible on site", type: "switch" },
];

export default function SlidersManager() {
  const { items, loading, busy, create, update, remove, move } = useCrud("/admin/cms/slides");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const openNew = () => { setEditing({ enabled: true }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setOpen(true); };
  const submit = async (form) => {
    if (editing?.id) await update(editing.id, form); else await create(form);
    setOpen(false);
  };

  return (
    <>
      <PageHeader
        title="Slider Manager"
        subtitle="Hero carousel on the homepage. Reorder, toggle visibility, or replace banner images."
        actions={<Button onClick={openNew} className="bg-[#0057B8] hover:bg-[#003F87] text-white rounded-full" data-testid="new-slider-btn"><Plus className="w-4 h-4 mr-1" /> New Slide</Button>}
      />
      {loading ? <div className="text-slate-500">Loading…</div> : items.length === 0 ? (
        <EmptyState label="No slides yet." action={<Button onClick={openNew}>Add first slide</Button>} />
      ) : (
        <div className="grid gap-4">
          {items.map((it, i) => (
            <GlassCard key={it.id} className="p-4">
              <div className="flex gap-4">
                <img src={it.image} alt="" className="w-40 h-24 object-cover rounded-lg border border-slate-200 shrink-0 bg-slate-100" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-[#B45309] bg-amber-100 px-2 py-0.5 rounded-full">{it.tag}</span>
                    {!it.enabled && <span className="text-[10px] font-semibold bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">Hidden</span>}
                  </div>
                  <div className="font-display font-semibold text-slate-900 mt-1 truncate">{it.title}</div>
                  <div className="text-sm text-slate-500 line-clamp-2">{it.subtitle}</div>
                  <div className="text-xs text-slate-400 mt-1">CTA: {it.cta_label || "—"} → {it.cta_to || "—"}</div>
                </div>
                <RowActions
                  onEdit={() => openEdit(it)}
                  onDelete={() => remove(it.id)}
                  onUp={() => move(it.id, "up")} onDown={() => move(it.id, "down")}
                  canUp={i > 0} canDown={i < items.length - 1}
                />
              </div>
            </GlassCard>
          ))}
        </div>
      )}
      <ItemDialog
        open={open} onOpenChange={setOpen}
        title={editing?.id ? "Edit Slide" : "New Slide"}
        fields={FIELDS}
        initial={editing || { enabled: true }}
        onSubmit={submit}
        submitting={busy}
      />
    </>
  );
}
